import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { 
  getAllZodiacMessagesApi, 
  updateZodiacMessageApi, 
  deleteZodiacMessageApi,
  getZodiacSignOptions,
  validateMessageTemplate,
  generateMessagePreview,
  seedZodiacMessagesApi,
  getZodiacMessageStatsApi
} from "../../services/zodiacMessageManagement";
import { SecondaryButton } from "../../components/Button/Button";
import Switch from "../../components/Switch/Switch";
import ModalBox from "../../components/modal/ModalBox";
import { IoSearch } from "react-icons/io5";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "react-js-pagination";

/**
 * Zodiac Message Management Component
 * 
 * Admin interface for managing personalized zodiac message templates.
 * Features:
 * - View all zodiac message configurations
 * - Edit message templates with {{name}} placeholder
 * - Toggle active/inactive status
 * - Delete configurations
 * - Preview personalized messages
 * - Bulk operations
 */
const ZodiacMessageManagement = () => {
  // State management
  const [zodiacMessages, setZodiacMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Modal states
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [seedModal, setSeedModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    zodiacSign: "",
    displayName: "",
    messageTemplate: "",
    personalizationInstructions: "",
    wordLimit: 35,
    isActive: true
  });
  const [previewName, setPreviewName] = useState("John");
  
  // Hooks
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  
  // Zodiac sign options
  const zodiacOptions = getZodiacSignOptions();

  /**
   * Fetch zodiac messages from API
   */
  const fetchZodiacMessages = async () => {
    try {
      showLoader();
      const params = {
        page: activePage,
        limit: 10,
        ...(statusFilter && { isActive: statusFilter })
      };
      
      const data  = await getAllZodiacMessagesApi(params);
      console.log("data", data);
      if (data?.code === 200) {
        setZodiacMessages(data.result.zodiacMessages || []);
        setTotal(data.result.total || 0);
      } else {
        toast.error(data.message || "Failed to fetch zodiac messages");
      }
    } catch (error) {
      console.error("Error fetching zodiac messages:", error);
      toast.error("Error fetching zodiac messages");
    } finally {
      hideLoader();
    }
  };

  /**
   * Handle edit button click
   */
  const handleEdit = (message) => {
    setSelectedMessage(message);
    setFormData({
      zodiacSign: message.zodiacSign,
      displayName: message.displayName,
      messageTemplate: message.messageTemplate,
      personalizationInstructions: message.personalizationInstructions || "",
      wordLimit: message.wordLimit || 35,
      isActive: message.isActive
    });
    setEditModal(true);
  };

  /**
   * Handle delete button click
   */
  const handleDelete = (message) => {
    setSelectedMessage(message);
    setDeleteModal(true);
  };

  /**
   * Handle preview button click
   */
  const handlePreview = (message) => {
    setSelectedMessage(message);
    setPreviewModal(true);
  };

  /**
   * Update zodiac message
   */
  const handleUpdate = async () => {
    try {
      // Validate message template
      const validation = validateMessageTemplate(formData.messageTemplate);
      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }

      showLoader();
      const data  = await updateZodiacMessageApi(formData);
      if (data.code === 200) {
        toast.success("Zodiac message updated successfully");
        setEditModal(false);
        fetchZodiacMessages();
      } else {
        toast.error(data.message || "Failed to update zodiac message");
      }
    } catch (error) {
      console.error("Error updating zodiac message:", error);
      toast.error("Error updating zodiac message");
    } finally {
      hideLoader();
    }
  };

  /**
   * Delete zodiac message
   */
  const handleDeleteConfirm = async () => {
    try {
      showLoader();
      const data  = await deleteZodiacMessageApi(selectedMessage.zodiacSign);
      if (data.code === 200) {
        toast.success("Zodiac message deleted successfully");
        setDeleteModal(false);
        fetchZodiacMessages();
      } else {
        toast.error(data.message || "Failed to delete zodiac message");
      }
    } catch (error) {
      console.error("Error deleting zodiac message:", error);
      toast.error("Error deleting zodiac message");
    } finally {
      hideLoader();
    }
  };

  /**
   * Handle pagination
   */
  const handlePageChange = (page) => {
    setActivePage(page);
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setActivePage(1);
  };

  /**
   * Handle seeder button click
   */
 

 
  // Effects
  useEffect(() => {
    fetchZodiacMessages();
  }, [activePage, statusFilter]);

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          {/* Header */}
          <div className="TitleBox">
            <h4 className="Title">Zodiac Message Management</h4>
            <p className="text-muted">Manage personalized horoscope messages for each zodiac sign</p>
         
          </div>

          {/* Filters */}
          <div className="FilterBox FilterArea">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Search */}
              <div className="form-group search_lg_size">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search zodiac signs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="user_search_icon">
                  <IoSearch />
                </span>
              </div>

              {/* Status Filter */}
              <div className="form-group select_sm_width">
                <select
                  className="form-control"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Zodiac Sign</th>
                  <th>Display Name</th>
                  <th>Message Template</th>
                  <th>Word Limit</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {zodiacMessages.length > 0 ? (
                  zodiacMessages.map((message, index) => (
                    <tr key={message._id}>
                      <td>{(activePage - 1) * 10 + index + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>
                            {zodiacOptions.find(opt => opt.value === message.zodiacSign)?.emoji}
                          </span>
                          <span style={{ textTransform: 'capitalize' }}>
                            {message.zodiacSign}
                          </span>
                        </div>
                      </td>
                      <td>{message.displayName}</td>
                      <td>
                        <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {message.messageTemplate}
                        </div>
                      </td>
                      <td>{message.wordLimit}</td>
                      <td>
                        <Switch
                          checked={message.isActive}
                          onChange={() => {
                            setFormData({ ...message, isActive: !message.isActive });
                            handleUpdate();
                          }}
                        />
                      </td>
                      <td>{new Date(message.updatedAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(message)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handlePreview(message)}
                            title="Preview"
                          >
                            <VisibilityIcon fontSize="small" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(message)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                      No zodiac messages found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 10 && (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={10}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <ModalBox
          status={editModal}
          onClose={() => setEditModal(false)}
          title="Edit Zodiac Message"
          action={handleUpdate}
          actionText="Update"
        >
          <div style={{ padding: '20px' }}>
            <div className="form-group">
              <label>Zodiac Sign</label>
              <input
                type="text"
                className="form-control"
                value={formData.zodiacSign}
                disabled
              />
            </div>
            
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="Enter display name"
              />
            </div>
            
            <div className="form-group">
              <label>Message Template (Use {`{{name}}`} for personalization)</label>
              <textarea
                className="form-control"
                rows="4"
                value={formData.messageTemplate}
                onChange={(e) => setFormData({ ...formData, messageTemplate: e.target.value })}
                placeholder="Enter message template with {{name}} placeholder"
              />
              <small className="text-muted">
                Character count: {formData.messageTemplate.length}/500
              </small>
            </div>
            
            <div className="form-group">
              <label>Personalization Instructions</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.personalizationInstructions}
                onChange={(e) => setFormData({ ...formData, personalizationInstructions: e.target.value })}
                placeholder="Instructions for AI personalization"
              />
            </div>
            
            <div className="form-group">
              <label>Word Limit</label>
              <input
                type="number"
                className="form-control"
                value={formData.wordLimit}
                onChange={(e) => setFormData({ ...formData, wordLimit: parseInt(e.target.value) })}
                min="10"
                max="100"
              />
            </div>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                {' '}Active
              </label>
            </div>
          </div>
        </ModalBox>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <ModalBox
          status={deleteModal}
          onClose={() => setDeleteModal(false)}
          title="Delete Zodiac Message"
          description={`Are you sure you want to delete the ${selectedMessage?.zodiacSign} zodiac message?`}
          action={handleDeleteConfirm}
          actionText="Delete"
        />
      )}

      {/* Preview Modal */}
      {previewModal && (
        <ModalBox
          status={previewModal}
          onClose={() => setPreviewModal(false)}
          title="Message Preview"
        >
          <div style={{ padding: '20px' }}>
            <div className="form-group">
              <label>Test Name</label>
              <input
                type="text"
                className="form-control"
                value={previewName}
                onChange={(e) => setPreviewName(e.target.value)}
                placeholder="Enter test name"
              />
            </div>
            
            <div className="form-group">
              <label>Preview Message</label>
              <div style={{ 
                padding: '15px', 
                border: '1px solid #ddd', 
                borderRadius: '5px',
                backgroundColor: '#f9f9f9',
                minHeight: '100px'
              }}>
                {generateMessagePreview(selectedMessage?.messageTemplate, previewName)}
              </div>
            </div>
          </div>
        </ModalBox>
      )}

    
    </>
  );
};

export default ZodiacMessageManagement;
